import React, { useState, useRef, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Row, Col, Space, Card, notification } from 'antd';
import { SearchOutlined, EnvironmentOutlined, EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Highlighter from 'react-highlight-words';
import DefaultLayout from '../../components/layout/DefaultLayout';
import Header from '../../components/header';
import equipmentService from '../../services/equipments';
import { Excel } from "antd-table-saveas-excel";

const Equipments = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [editing, setEditing] = useState(null);

  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const searchInput = useRef(null);

  const getEquipments = async (page = 1, search = '') => {
    setLoading(true);

    await equipmentService.getEquipmentsUP(page, itemsPerPage, search).then((response) => {
      setData(response.data);
      setCurrentPage(page);
      setTotalDataCount(response.total);
      setItemsPerPage(response.per_page);
      setLoading(false);
    });
  };

  useEffect(() => {
    getEquipments();
  }, []);

  const handleClick = () => {
    const excelColumns = columns
      .filter((column) => column.key !== "Actions")
      .map((column) => {
      const { render, ...columnWithoutRender } = column;
      return columnWithoutRender;
      });

    const excel = new Excel();
    excel
      .addSheet("data")
      .addColumns(excelColumns)
      .addDataSource(data, {
      str2Percent: true,
      })
      .saveAs("cost_centers.xlsx");
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
  };

  const handleInputChange = (event) => {
    const newSearchQuery = event.target.value;
    setSearchQuery(newSearchQuery);
    if (newSearchQuery.length > 1 || newSearchQuery === '') {
      getEquipments(1, newSearchQuery);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: `Are you sure you want to delete the Equipment: ${record.name}?`,
      onOk: async () => {
        setLoading(true);
        try {
          const response = await equipmentService.deleteEquipment(record.id);
          if (response.success) {
            notification.success({
              message: 'Equipment Deleted',
              description: response.message,
            });
            getEquipments(currentPage);
          }
        } catch (error) {
          notification.error({
            message: 'Error',
            description: error.response.data.message,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: text =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text}
        />
      ) : (
        text
      ),
  });

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditing(null);
    form.resetFields();
  };

  const handleEdit = record => {
    setEditing(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (editing) {
        const response = await equipmentService.updateEquipment(editing.id, values);

        if (response.success) {
          setIsModalVisible(false);
          setEditing(null);
          form.resetFields();
          notification.success({
            message: 'Equipment Updated',
            description: response.message,
          });

          getEquipments(currentPage);
        }

      } else {
        const response = await equipmentService.postEquipment(values);

        if (response.success) {
          
          setIsModalVisible(false);
          setEditing(null);
          form.resetFields();

          notification.success({
            message: 'Equipment Added',
            description: response.message,
          });

          getEquipments(currentPage);
        }

      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response.data.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const viewHistory = async (record) => {
    setIsHistoryModalVisible(true);
    setHistoryLoading(true);
    try {
      const response = await equipmentService.getPriceHistory(record.id);
      setHistoryData(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching history',
        description: error.message,
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const historyColumns = [
    {
      title: 'Old Price',
      dataIndex: 'old_price',
      key: 'old_price',
      render: (val) => val ?? 'N/A',
    },
    {
      title: 'New Price',
      dataIndex: 'new_price',
      key: 'new_price',
    },
    {
      title: 'Updated By',
      dataIndex: 'updater',
      key: 'updater',
      render: (updater) => updater?.name || 'Unknown',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val) => dayjs(val).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  const columns = [
    {
      title: 'Equipment Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      // ...getColumnSearchProps('name'),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 80,
      render: (text, record) => (
        <Space>
          <div
            onClick={() => handleEdit(record)}
            style={{ cursor: 'pointer' }}
          >
            <EditOutlined />
          </div>

          <div
            onClick={() => viewHistory(record)}
            style={{ cursor: 'pointer' }}
            title="View Price History"
          >
            <HistoryOutlined />
          </div>

          <div
            onClick={() => handleDelete(record)}
            style={{ cursor: 'pointer' }}
          >
            <DeleteOutlined />
          </div>
        </Space>
      ),
    },
  ];

  return (
    <DefaultLayout>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Header
            icon={<EnvironmentOutlined />}
            title="Equipments"
            right={(
              <>
              <Button onClick={handleClick} className='mr-10'>
                Export
              </Button>
              <Button className='btn-blue' type="primary" onClick={showModal}>
                Add Equipment
              </Button>
              </>
            )}
          />
        </Col>
      </Row>

      <Card>
        <Input
          size='large'
          placeholder='Search'
          allowClear
          value={searchQuery}
          onChange={handleInputChange}
        />
      </Card>

      <Card>
        <Table
          style={{ minHeight: '100vh' }}
          columns={columns}
          dataSource={data.map((item) => ({ ...item, key: item.id }))}
          loading={loading}
          pagination={{
            total: totalDataCount,
            current: currentPage,
            pageSize: itemsPerPage,
            showSizeChanger: false,
            onChange: (page, pageSize) => getEquipments(page, searchQuery),
          }}
        />
      </Card>

      <Modal
        title={editing ? 'Edit Equipment' : 'Add Equipment'}
        open={isModalVisible}
        onCancel={handleCancel}
        width={650}
        footer={null}
        destroyOnClose={true}
      >
        <Form form={form} onFinish={onFinish} className='mt-20' layout="vertical">
          <Form.Item
            name="name"
            label="Equipment Name"
            rules={[
              { required: true, message: 'Please enter the Equipment' },
            ]}
          >
            <Input size='large' placeholder="Type Equipment Name" />
          </Form.Item>
          <Form.Item
            name="price"
            label="Price"
          >
            <Input type="number" step="0.01" size='large' placeholder="Type Price (Optional)" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editing ? 'Save Changes' : 'Add Equipment'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Price History"
        open={isHistoryModalVisible}
        onCancel={() => setIsHistoryModalVisible(false)}
        footer={null}
        width={700}
      >
        <Table
          columns={historyColumns}
          dataSource={historyData.map(item => ({ ...item, key: item.id }))}
          loading={historyLoading}
          pagination={false}
        />
      </Modal>
    </DefaultLayout>
  );
};

export default Equipments;
